import { useState, useEffect } from 'react';
import { X, Share, PlusSquare, Download } from 'lucide-react';
import { useTranslation } from '../../core/hooks/useTranslation';

export const InstallPrompt = () => {
    const { t } = useTranslation();
    const [isIOS, setIsIOS] = useState(false);
    const [isStandalone, setIsStandalone] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        // 1. Check if already installed
        const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches
            || (window.navigator as any).standalone
            || document.referrer.includes('android-app://');

        setIsStandalone(isStandaloneMode);

        if (isStandaloneMode) return;

        // 2. Check dismissal history
        const dismissedAt = localStorage.getItem('pwaPromptDismissedAt');
        if (dismissedAt) {
            const daysSinceDismissal = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
            if (daysSinceDismissal < 7) {
                return; // Respect 7-day dismissal window
            }
        }

        // 3. Detect iOS Safari
        const userAgent = window.navigator.userAgent.toLowerCase();
        const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
        setIsIOS(isIOSDevice);

        if (isIOSDevice) {
            setIsVisible(true);
        }

        // 4. Capture Android/Chrome install prompt
        const handleBeforeInstallPrompt = (e: any) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setIsVisible(true);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    const handleDismiss = () => {
        setIsVisible(false);
        localStorage.setItem('pwaPromptDismissedAt', Date.now().toString());
    };

    const handleInstallClick = async () => {
        if (!deferredPrompt) return;

        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;

        if (outcome === 'accepted') {
            setIsVisible(false);
        }
        setDeferredPrompt(null);
    };

    if (!isVisible || isStandalone) return null;

    return (
        <div className="fixed bottom-4 left-4 right-4 z-[9999] md:bottom-8 md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-md animate-slide-up pointer-events-auto">
            <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.15)] border border-gray-100 p-4 relative overflow-hidden">
                {/* Close Button */}
                <button
                    onClick={handleDismiss}
                    className="absolute top-2 right-2 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                >
                    <X className="w-5 h-5" />
                </button>

                <div className="flex items-start gap-4">
                    {/* App Icon */}
                    <img
                        src="/app-icon.jpg"
                        alt="HESN App"
                        className="w-14 h-14 rounded-xl shadow-sm border border-gray-100 object-cover shrink-0"
                    />

                    <div className="flex-1 pr-6 pt-1">
                        <h4 className="font-bold text-secondary">
                            {t('pwa.install.title', 'Install HESN App')}
                        </h4>

                        {isIOS ? (
                            <div className="text-sm text-gray-600 mt-1 space-y-2">
                                <p>{t('pwa.install.ios.desc', 'Install this webapp on your iPhone for quick access.')}</p>
                                <div className="flex items-center gap-2 bg-gray-50 p-2 rounded-lg text-xs font-medium border border-gray-100">
                                    <span>Tap</span>
                                    <Share className="w-4 h-4 text-blue-500" />
                                    <span>then</span>
                                    <PlusSquare className="w-4 h-4 text-gray-600" />
                                    <span>"Add to Home Screen"</span>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <p className="text-sm text-gray-600 mt-1">
                                    {t('pwa.install.android.desc', 'Get the app on your device for a faster, better experience.')}
                                </p>
                                <button
                                    onClick={handleInstallClick}
                                    className="mt-3 bg-secondary hover:bg-secondary-dark text-white text-sm font-bold px-5 py-2 rounded-full w-full transition-colors flex items-center justify-center gap-2 shadow-md shadow-secondary/20"
                                >
                                    <Download className="w-4 h-4" />
                                    {t('pwa.install.btn', 'Install App')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <style>{`
                 @keyframes slide-up {
                    from { transform: translateY(150%); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                 }
                 .animate-slide-up {
                    animation: slide-up 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                 }
            `}</style>
        </div>
    );
};
